import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').Medicine} Medicine
 * @typedef {import('./state').MedicineConfiguration} MedicineConfiguration
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetMedicinesActionOpts
 * @property {string} searchString
 * @property {boolean} fixturesOnly
 * @property {boolean} customOnly
 *
 * @callback GetMedicinesAction
 * @param {ActionContext} ctx
 * @param {GetMedicinesActionOpts} opts
 * @returns {Promise<Medicine[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetMedicinesAction}
 */
export const getMedicines = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  // fetch
  const query = {
    $populate: {
      formulations: {
        service: 'medicine-formulations',
        method: 'find',
        foreignKey: 'medicine',
        localKey: 'id'
      }
    }
  };
  if (opts.fixturesOnly) query.createdBy = null;
  if (opts.customOnly) query.createdBy = { $exists: true };
  if (opts.searchString) {
    query.$or = [
      { genericName: { $regex: `^${opts.searchString}`, $options: `i` } },
      { brandName: { $regex: `^${opts.searchString}`, $options: `i` } }
    ];
  }
  const medicines = await sdk.medicine.medicines()
    .find(query)
    .then(({ items }) => items.map(({ $populated, ...i }) => ({ ...i, ...$populated })));
  ctx.commit('updateInMedicines', { items: medicines, upsert: true });

  return medicines;
};

/**
 * @typedef {Object} GetFavoriteMedicinesActionOpts
 * @property {string} searchString
 *
 * @callback GetFavoriteMedicinesAction
 * @param {ActionContext} ctx
 * @param {GetFavoriteMedicinesActionOpts} opts
 * @returns {Promise<MedicineConfiguration[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetFavoriteMedicinesAction}
 */
export const getFavoriteMedicines = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  // fetch
  const query = { };
  const medicines = await sdk.medicine.configurations().find(query).then(r => r.items);
  ctx.commit('updateInFavoriteMedicines', { items: medicines, upsert: true });

  return medicines;
};

/**
 * @typedef {Object} CreateMedicineActionOpts
 * @property {string} genericName
 * @property {string} brandName
 * @property {string} formulation
 *
 * @callback CreateMedicineAction
 * @param {ActionContext} ctx
 * @param {CreateMedicineActionOpts} opts
 * @returns {Promise<Medicine>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateMedicineAction}
 */
export const createMedicine = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const { genericName, brandName, formulation } = opts;
  if (!genericName) throw new Error(`${nms}: Generic name is required`);
  const data = {
    genericName,
    brandName,
    formulations: [ formulation && { formulation } ].filter(Boolean)
  };
  const medicine = await sdk.medicine.medicines().create(data);
  ctx.commit('updateInMedicines', { upsert: true, items: [ medicine ] });

  return medicine;
};

/**
 * @typedef {Object} CreateFavoriteMedicineActionOpts
 * @property {MedicineConfiguration[]} items
 *
 * @callback CreateFavoriteMedicineAction
 * @param {ActionContext} ctx
 * @param {CreateFavoriteMedicineActionOpts} opts
 * @returns {Promise<MedicineConfiguration>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateFavoriteMedicineAction}
 */
export const createFavoriteMedicines = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const { items } = opts;
  const configurations = await sdk.medicine.configurations().create(items);
  ctx.commit('updateInFavoriteMedicines', { upsert: true, items: configurations });

  return configurations;
};
